/** Signals to a CancellationToken that it should be canceled. */
export class CancellationTokenSource {
    private timerHandle = 0;
    private disposed = false;
    private canceled = false;
    private readonly cancellationToken: CancellationToken;

    /**
     * Initializes a new instance of the CancellationTokenSource class.
     * @param millisecondsDelay The optional time interval in milliseconds to wait before canceling.
     */
    constructor(millisecondsDelay?: number) {
        this.cancellationToken = new CancellationToken(this);

        if (millisecondsDelay) {
            this.cancelAfter(millisecondsDelay);
        }
    }

    /** Gets a value indicating whether cancellation has been requested. */
    public get isCancellationRequested(): boolean {
        return this.canceled;
    }

    /** Gets the CancellationToken associated with this CancellationTokenSource. */
    public get token(): CancellationToken {
        return this.cancellationToken;
    }

    /**
     * Creates a CancellationTokenSource that will be in the canceled state when any of the source tokens
     * in the specified array are in the canceled state.
     * @param tokens An array that contains the cancellation token instances to observe.
     * @returns A CancellationTokenSource that is linked to the source tokens.
     */
    public static createLinkedTokenSource(...tokens: CancellationToken[]): CancellationTokenSource {
        const source = new CancellationTokenSource();

        tokens.forEach((token) => {
            token.register(source.cancel.bind(this));
        });

        return source;
    }

    /** Communicates a request for cancellation. */
    public cancel(): void {
        this.canceled = true;
        this.cancelTimer();
    }

    /**
     * Schedules a cancel operation on this CancellationTokenSource after the specified number of milliseconds.
     * @param millisecondsDelay The time span to wait before canceling this CancellationTokenSource.
     */
    public cancelAfter(millisecondsDelay: number): void {
        this.cancelTimer();
        this.timerHandle = setTimeout(<TimerHandler>this.cancel.bind(this), millisecondsDelay);
    }

    /** Releases all resources used by the current instance of the CancellationTokenSource class. */
    public dispose(): void {
        if (this.disposed) {
            return;
        }

        this.disposed = true;
        this.cancelTimer();
    }

    private cancelTimer(): void {
        if (this.timerHandle === 0) {
            return;
        }

        clearTimeout(this.timerHandle);
        this.timerHandle = 0;
    }
}

/** Propagates notification that operations should be canceled. */
export class CancellationToken {
    private static none: CancellationToken = null;
    private readonly parentCancel: () => void;
    private readonly callbacks = new Array<() => void>();

    /**
     * Initializes a new instance of the CancellationToken class.
     * @param source The underlying CancellationTokenSource.
     */
    constructor(private readonly source: CancellationTokenSource) {
        // this is a 'bait & switch' because only a CTS is allowed to trigger cancellation
        const originalCancel = source.cancel;
        this.parentCancel = () => originalCancel.call(this.source);
        this.source.cancel = () => this.cancel();
    }

    /** Returns an empty CancellationToken value. */
    public static get NONE(): CancellationToken {
        if (CancellationToken.none === null) {
            CancellationToken.none = new CancellationTokenSource().token;
        }
        return CancellationToken.none;
    }

    /** Gets a value indicating whether cancellation has been requested for this token. */
    public get isCancellationRequested(): boolean {
        return this.source.isCancellationRequested;
    }

    /** Throws an Error if this token has had cancellation requested. */
    public throwIfCancellationRequested(): void {
        if (this.isCancellationRequested) {
            throw new Error('The operation was canceled.');
        }
    }

    /**
     * Registers a function that will be called when this CancellationToken is canceled.
     * @param callback The function to be executed when the CancellationToken is canceled.
     */
    public register(callback: () => void): void {
        this.callbacks.push(callback);
    }

    private cancel(): void {
        if (this.isCancellationRequested) {
            return;
        }

        this.parentCancel();
        this.callbacks.forEach((callback) => {
            callback();
        });
    }
}
